import {
    WIDGET_DEFINITIONS,
    buildInstallGuide,
    widgetTypesFor,
    type InstallGuide,
    type ReviewSourceId,
    type WebsitePlatform,
    type WidgetType,
} from '@flexbyt/shared';
import { ALL_INTEGRATIONS, integrationBySlug } from './integrations.js';
import { PLATFORM_CONTENT, type IntegrationDepth } from './platforms.js';

/**
 * Source × destination pages: "Google reviews widget for Webflow".
 *
 * ===========================================================================
 * Why this is a curated list and not a loop
 * ===========================================================================
 * Nine sources times eight platforms is seventy-two routes, and generating them
 * would produce exactly the thin programmatic SEO the brief rules out — the
 * same four paragraphs with two nouns substituted, which reads as thin to a
 * person long before a search engine decides anything.
 *
 * So the architecture scales and the *published set* does not. A combination
 * appears here only when there is something true to say about that specific
 * pairing that is not already said on the source page or the platform page. An
 * Airbnb host on Squarespace and a law firm on Webflow are different problems;
 * "Etsy reviews for GoDaddy" is a phrase nobody types.
 *
 * ===========================================================================
 * What is written and what is read
 * ===========================================================================
 * The *facts* on these pages come from the product and are never authored here:
 *
 *   installation      `buildInstallGuide` — the same function the dashboard's
 *                     install sheet calls, so the steps shown are the steps a
 *                     customer is actually given
 *   which widgets     `widgetTypesFor`, which knows the product widget needs a
 *                     Wix store
 *   shortcode or not  `WIDGET_DEFINITIONS[type].shortcode`, which is why the
 *                     WordPress popup is described differently from the rest
 *   how deep          `PLATFORM_CONTENT[platform].depth` — app, plugin, or an
 *                     embed you paste
 *
 * What is written per combination is the part a machine cannot produce: why
 * somebody with these reviews and this website has this problem.
 */

export type Combination = {
  source: ReviewSourceId;
  platform: WebsitePlatform;

  /** Why this pairing specifically, in one or two sentences. */
  why: string;
  /** Things true of this pair that are not true of either half alone. */
  notes: string[];
  /** The layout that suits this pairing, and the reason. */
  recommended: WidgetType;
  recommendedWhy: string;
  faqs: { q: string; a: string }[];
};

/**
 * The published set.
 *
 * Chosen for intent rather than coverage: local businesses are on Wix,
 * Squarespace and WordPress; hotels are on WordPress and Squarespace; agencies
 * and companies are on Webflow. Combinations nobody searches for are left out
 * rather than filled in, and adding one later means writing its copy — which is
 * the point of the shape of this file.
 */
export const COMBINATIONS: Combination[] = [
  // --- Google ---------------------------------------------------------------
  {
    source: 'google',
    platform: 'wix',
    why: 'Wix is where a great many local businesses build their first site, and Google is where their customers already leave reviews. The two go together more often than any other pairing ReviewsJet sees.',
    notes: [
      'Wix installs ReviewsJet as an app, so you sign in from your Wix dashboard rather than keeping a second password.',
      'Widgets are added in the Wix Editor as a Custom Element, not as pasted HTML — Wix has its own way of doing this and the guide below follows it.',
    ],
    recommended: 'grid',
    recommendedWhy:
      'Wix pages are built from sections with fixed widths, and an even grid drops into one without fighting the layout around it.',
    faqs: [
      {
        q: 'Do I need to know any code?',
        a: 'No. You add a Custom Element in the Wix Editor and fill in three fields — a server address, a tag name and your site id. ReviewsJet gives you all three.',
      },
      {
        q: 'Will it slow my Wix site down?',
        a: 'The widget loads after your page does and fetches reviews separately, so it does not hold up anything a visitor is waiting for.',
      },
    ],
  },
  {
    source: 'google',
    platform: 'webflow',
    why: 'Webflow sites are usually built by somebody who cares how things look, for a client who wants their Google rating visible. The widget has to fit a design rather than sit beside it.',
    notes: [
      'ReviewsJet is a Webflow app, so installing it connects your site — you are not pasting a script into a project you may not own.',
      'The popup is the one layout Webflow installs for you: ReviewsJet registers the script on your site and you only have to publish.',
    ],
    recommended: 'masonry',
    recommendedWhy:
      'Webflow projects tend to have a dedicated page or a full-width section to give reviews, and masonry is the layout that fills one without leaving gaps.',
    faqs: [
      {
        q: 'Why do I not see the widget in the Designer?',
        a: 'Webflow only renders Embed elements on the published site, never in the Designer canvas. Publish once and it appears.',
      },
      {
        q: 'Can I style it to match my Webflow design?',
        a: 'Card colour, text colour, border, corner radius, font size and what each card shows are all settings. It is not styled with Webflow classes — the widget brings its own styling so it stays consistent wherever you put it.',
      },
    ],
  },
  {
    source: 'google',
    platform: 'wordpress',
    why: 'More small business sites run on WordPress than anything else, and most of those businesses have a Google Business Profile they have never used anywhere but Google.',
    notes: [
      'WordPress uses the ReviewsJet plugin and a shortcode rather than pasted HTML. That is not a preference: a script tag pasted into a Custom HTML block is stripped by a great many themes and security plugins, so it would render nothing and give you no clue why.',
      'The shortcode goes anywhere a shortcode goes — a page, a post, a widget area, a template.',
    ],
    recommended: 'list',
    recommendedWhy:
      'WordPress themes vary wildly in the width they give a content column, and the list is the one layout that is untroubled by a narrow one.',
    faqs: [
      {
        q: 'Which shortcode do I use?',
        a: 'One per layout — [rj_slider], [rj_marquee], [rj_masonry], [rj_grid] and [rj_list]. ReviewsJet shows you the exact shortcode with your site id already in it.',
      },
      {
        q: 'Is there a shortcode for the popup?',
        a: 'No. The popup is site-wide rather than placed on a page, so it goes in before the closing body tag — through Appearance → Theme File Editor, or any "insert headers and footers" plugin.',
      },
      {
        q: 'Does it work with WooCommerce?',
        a: 'Yes. WooCommerce runs on WordPress, so the plugin and its shortcodes behave exactly the same.',
      },
    ],
  },
  {
    source: 'google',
    platform: 'squarespace',
    why: 'Squarespace is chosen by people who want a good-looking site without maintaining one, and a Google rating is the one thing they cannot get onto it without help.',
    notes: [
      'There is no ReviewsJet app for Squarespace. You paste an embed into a Code block, which works completely — it is just not an integration, and this page will not call it one.',
      'Code blocks only render on the published site, so the Squarespace editor will show you a placeholder rather than your reviews.',
    ],
    recommended: 'grid',
    recommendedWhy:
      'Squarespace templates are built on a tidy grid of their own, and a review grid sits inside one without looking like something bolted on.',
    faqs: [
      {
        q: 'Why can I not see it while editing?',
        a: 'Squarespace does not run code blocks in the editor. Open your published page and it will be there.',
      },
      {
        q: 'Do I need a paid Squarespace plan?',
        a: 'Code blocks need a Business plan or above on Squarespace. That is their rule rather than ours — the popup is an alternative, since it goes in Code Injection.',
      },
    ],
  },

  // --- Tripadvisor ----------------------------------------------------------
  {
    source: 'tripadvisor',
    platform: 'wordpress',
    why: 'Independent hotels and guesthouses run WordPress more than any other platform, and the reviews that fill their rooms are sitting on Tripadvisor earning commission for somebody else.',
    notes: [
      'The ReviewsJet plugin means a shortcode you can drop into a room page, a booking page or the theme itself.',
      'Guest photographs come across where a review has them, which matters more for a hotel than it does for most businesses.',
    ],
    recommended: 'masonry',
    recommendedWhy:
      'Hotel reviews are long and often carry photographs, and masonry sizes each card around its own content instead of padding the short ones.',
    faqs: [
      {
        q: 'Can I show reviews on the page for one room?',
        a: 'Tag the reviews that mention it and point a widget at that tag. Tripadvisor reviews are about the property rather than the room, so this works best where the tags are ones you decide.',
      },
      {
        q: 'What about my restaurant’s separate listing?',
        a: 'Connect it as its own source. Both arrive in one library, still labelled, so the restaurant page can show restaurant reviews.',
      },
    ],
  },
  {
    source: 'tripadvisor',
    platform: 'squarespace',
    why: 'Squarespace is the default for small hotels, B&Bs and restaurants that want something handsome without hiring anybody — and every one of them has a Tripadvisor listing doing the persuading.',
    notes: [
      'A Code block on the page where people book is the whole installation.',
      'No Squarespace app exists. The embed works everywhere on the platform; it is not a native integration and this page will not pretend otherwise.',
    ],
    recommended: 'slider',
    recommendedWhy:
      'A Squarespace booking page is usually one long scroll, and a carousel gives it social proof in a fixed height rather than another screenful.',
    faqs: [
      {
        q: 'Where should I put it?',
        a: 'Directly above whatever takes the booking. Reviews are most persuasive at the moment somebody is deciding, not at the bottom of the page.',
      },
      {
        q: 'Will the photographs work?',
        a: 'Yes, where a review has them. On a small screen they become thumbnails that open at full size.',
      },
    ],
  },
  {
    source: 'tripadvisor',
    platform: 'wix',
    why: 'Wix is common among attractions, tour desks and small restaurants — businesses whose Tripadvisor rating is the first thing a visitor checks and the last thing on their own site.',
    notes: [
      'The ReviewsJet Wix app signs you in from the Wix dashboard, so there is no second login to remember.',
      'Widgets are placed in the Editor as a Custom Element, which means you can drop one into any section rather than only where HTML is allowed.',
    ],
    recommended: 'slider',
    recommendedWhy:
      'Wix sections have a fixed height, and a carousel is the layout that shows several reviews without growing the section it is in.',
    faqs: [
      {
        q: 'Can I put it on more than one page?',
        a: 'Yes — add the Custom Element wherever you want it. The same reviews are available to every widget on the site.',
      },
      {
        q: 'I have a Wix store. Does that change anything?',
        a: 'It adds the product widget, which shows one product’s reviews on its own page. That one is Wix-only, because it needs a product page to sit on.',
      },
    ],
  },

  // --- Airbnb ---------------------------------------------------------------
  {
    source: 'airbnb',
    platform: 'squarespace',
    why: 'Hosts building a direct booking site almost always build it on Squarespace, and the whole argument for booking direct is trust — which is exactly what years of Airbnb reviews supply.',
    notes: [
      'A Code block on the booking page is the entire setup.',
      'Managing several properties? Each listing connects on its own, so one property page can show that property’s own guests.',
    ],
    recommended: 'list',
    recommendedWhy:
      'Guest reviews are long and personal, and a single column gives them the room to be read rather than skimmed.',
    faqs: [
      {
        q: 'Can I show only one property’s reviews?',
        a: 'Yes. Connect each listing separately and point each page’s widget at the right one.',
      },
      {
        q: 'Are guest surnames shown?',
        a: 'Airbnb publishes first names only, so that is what arrives. You can hide names entirely if you prefer.',
      },
    ],
  },
  {
    source: 'airbnb',
    platform: 'wordpress',
    why: 'Property managers with more than a couple of listings tend to outgrow a site builder and end up on WordPress. The reviews are still on Airbnb, and still the reason anybody books.',
    notes: [
      'The plugin gives you a shortcode, which is what makes a per-property page practical — one shortcode per page, pointed at that property’s tag.',
      'Each listing is connected separately. There is no bulk import of a whole portfolio.',
    ],
    recommended: 'grid',
    recommendedWhy:
      'A portfolio site usually shows several properties at once, and an even grid keeps a page of them looking deliberate.',
    faqs: [
      {
        q: 'How do I keep each property’s reviews on its own page?',
        a: 'Tag reviews as they arrive and give each page a widget filtered to that tag.',
      },
      {
        q: 'Can I add twenty listings at once?',
        a: 'No — they are connected one at a time. ReviewsJet has no bulk or portfolio import, and this page will not suggest it does.',
      },
    ],
  },

  // --- Trustpilot -----------------------------------------------------------
  {
    source: 'trustpilot',
    platform: 'webflow',
    why: 'Webflow is where funded companies and the agencies that serve them build marketing sites, and Trustpilot is where those companies have already been collecting reviews for years.',
    notes: [
      'ReviewsJet is a Webflow app, so a designer can install it on a client site without handing round credentials.',
      'The popup installs itself here — ReviewsJet registers the script and you publish.',
    ],
    recommended: 'slider',
    recommendedWhy:
      'A Webflow marketing page usually wants social proof in one band between sections, and a carousel is that band.',
    faqs: [
      {
        q: 'Can I use it on a client’s site?',
        a: 'Yes. Each site is its own ReviewsJet workspace. There is no white-labelling and no agency dashboard — the widget carries a small ReviewsJet mark unless the workspace is on a paid plan.',
      },
      {
        q: 'Does it work in a Webflow CMS collection page?',
        a: 'Yes, as an Embed element inside the collection template. It shows the same workspace reviews on every item unless you filter by tag.',
      },
    ],
  },
  {
    source: 'trustpilot',
    platform: 'wordpress',
    why: 'Plenty of established companies run a WordPress marketing site alongside a Trustpilot profile they have been feeding for years — and never put one on the other.',
    notes: [
      'A shortcode on the pricing page is usually the highest-value place to put it.',
      'The plugin route matters here: security plugins are common on business WordPress sites and they strip pasted scripts.',
    ],
    recommended: 'grid',
    recommendedWhy:
      'Company reviews tend to be similar in length, which is exactly when a grid looks tidy rather than ragged.',
    faqs: [
      {
        q: 'Where does it help most?',
        a: 'Next to whatever asks for a decision — a pricing table, a contact form, a quote request.',
      },
      {
        q: 'Can I show only the best ones?',
        a: 'You publish reviews individually, so only what you publish appears.',
      },
    ],
  },

  // --- Yelp -----------------------------------------------------------------
  {
    source: 'yelp',
    platform: 'wordpress',
    why: 'Restaurants, salons and trades on WordPress usually have a Yelp page doing more work than their website — and no way to bring one to the other.',
    notes: [
      'A shortcode drops into a page, a post or a sidebar widget area.',
      'More than one location? Each Yelp business page connects separately and keeps its own label.',
    ],
    recommended: 'list',
    recommendedWhy:
      'Yelp reviews are conversational and often long, and most of this traffic is on a phone — which is one column either way.',
    faqs: [
      {
        q: 'Can each location have its own reviews?',
        a: 'Yes. Connect each Yelp page as its own source and filter each widget by tag.',
      },
      {
        q: 'Will it keep up with new Yelp reviews?',
        a: 'Yes — ReviewsJet re-checks your listing and brings new ones in without you doing anything.',
      },
    ],
  },
  {
    source: 'yelp',
    platform: 'squarespace',
    why: 'Squarespace suits restaurants and studios that want one good-looking page, and their Yelp rating is the thing that decides whether somebody books it.',
    notes: [
      'A Code block above the booking or contact section is the whole job.',
      'Code blocks need a Business plan on Squarespace; the popup is an alternative because it goes in Code Injection instead.',
    ],
    recommended: 'slider',
    recommendedWhy:
      'A single-page Squarespace site has no room for a wall of reviews, and a carousel gives it several in the height of one.',
    faqs: [
      {
        q: 'Can I put it in the footer so it shows everywhere?',
        a: 'You can, though the popup is the better tool for site-wide — it sits on every page without taking any layout.',
      },
      {
        q: 'Do the reviewer photos come across?',
        a: 'Where Yelp shows one. You can switch photos off and show names only.',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Lookups and derived facts
// ---------------------------------------------------------------------------

export function combinationFor(
  sourceSlug: string,
  platformSlug: string
): Combination | undefined {
  const integration = integrationBySlug(sourceSlug);
  if (!integration) return undefined;

  return COMBINATIONS.find(
    (entry) =>
      entry.source === integration.id &&
      PLATFORM_CONTENT[entry.platform].slug === platformSlug
  );
}

export function combinationPath(entry: Combination): string {
  const integration = ALL_INTEGRATIONS.find(
    (candidate) => candidate.id === entry.source
  )!;
  return `/integrations/${integration.slug}/${PLATFORM_CONTENT[entry.platform].slug}`;
}

/** Every combination published for a given platform — the platform hub's list. */
export function combinationsForPlatform(platform: WebsitePlatform): Combination[] {
  return COMBINATIONS.filter((entry) => entry.platform === platform);
}

/** Every combination published for a given source — shown on the source page. */
export function combinationsForSource(source: ReviewSourceId): Combination[] {
  return COMBINATIONS.filter((entry) => entry.source === source);
}

/** Platforms with at least one published combination. */
export function publishedPlatforms(): WebsitePlatform[] {
  return [...new Set(COMBINATIONS.map((entry) => entry.platform))];
}

/**
 * What this pairing can honestly claim.
 *
 * Every field is computed from the product rather than authored, so a page
 * cannot say "native Webflow integration" about a platform that only takes a
 * pasted embed, and cannot offer a WordPress popup shortcode that does not
 * exist.
 */
export type CombinationCapability = {
  depth: IntegrationDepth;
  /** "Native Wix app" / "Works on Squarespace". */
  claim: string;
  /** Layouts installable on this platform. */
  widgets: readonly WidgetType[];
  /** Layouts with a WordPress shortcode, where that is the install route. */
  shortcodes: WidgetType[];
  /** Layouts the platform installs without the user placing anything. */
  automatic: WidgetType[];
  /** The real steps, from the product's own install builder. */
  guide: InstallGuide;
};

export function capabilityFor(entry: Combination): CombinationCapability {
  const platformContent = PLATFORM_CONTENT[entry.platform];
  const widgets = widgetTypesFor(entry.platform);

  const guide = buildInstallGuide({
    widgetType: entry.recommended,
    // Obviously a placeholder. A realistic-looking id would be somebody's
    // actual workspace, and these pages are public.
    embedId: 'your-site-id',
    platform: entry.platform,
  });

  return {
    depth: platformContent.depth,
    claim: platformContent.claim,
    widgets,
    /*
      Only meaningful on WordPress, where the plugin is the install route. The
      popup and the product widget have no shortcode — the plugin has no notion
      of "the current product", and the popup is site-wide rather than placed.
    */
    shortcodes:
      entry.platform === 'wordpress'
        ? widgets.filter((type) => WIDGET_DEFINITIONS[type].shortcode !== null)
        : [],
    /*
      Webflow registers the popup script itself, which is the only case in the
      product where a widget needs no placement at all. Read from the guide
      rather than asserted, so it moves if the product changes.
    */
    automatic:
      buildInstallGuide({
        widgetType: 'popup',
        embedId: 'your-site-id',
        platform: entry.platform,
      }).automatic != null
        ? ['popup']
        : [],
    guide,
  };
}
